import React, { useState, useRef, useEffect, useCallback } from "react";

/*  */
const S = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=DM+Mono:wght@400;500&family=Noto+Sans+SC:wght@300;400;500;700&display=swap');

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    :root {
      --bg:     #0a0a0a;
      --bg2:    #111111;
      --bg3:    #1a1a1a;
      --bg4:    #222222;
      --line:   #2a2a2a;
      --line2:  #333333;
      --text1:  #f0ece4;
      --text2:  #a09888;
      --text3:  #605850;
      --gold:   #c8a850;
      --gold2:  #e8c870;
      --red:    #c84030;
      --red2:   #e85040;
      --green:  #48a878;
      --green2: #60c890;
      --blue:   #4878c8;
      --sh:     0 4px 24px rgba(0,0,0,.6);
    }

    html, body { background: var(--bg); color: var(--text1); font-family: 'Noto Sans SC', sans-serif; min-height: 100vh; }

    ::-webkit-scrollbar { width: 4px; }
    ::-webkit-scrollbar-track { background: var(--bg2); }
    ::-webkit-scrollbar-thumb { background: var(--line2); border-radius: 2px; }

    @keyframes fadeIn   { from{opacity:0} to{opacity:1} }
    @keyframes slideUp  { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
    @keyframes spin     { to{transform:rotate(360deg)} }
    @keyframes pulse    { 0%,100%{opacity:1} 50%{opacity:.4} }
    @keyframes scanLine { 0%{transform:translateY(-100%)} 100%{transform:translateY(100vh)} }
    @keyframes glow     { 0%,100%{box-shadow:0 0 10px rgba(200,168,80,.2)} 50%{box-shadow:0 0 30px rgba(200,168,80,.5)} }
    @keyframes typeIn   { from{width:0} to{width:100%} }
    @keyframes flicker  { 0%,100%{opacity:1} 8%{opacity:.85} 80%{opacity:.95} 85%{opacity:.8} }
    @keyframes barFill  { from{width:0} to{width:var(--target)} }
    @keyframes redPulse { 0%,100%{background:rgba(200,64,48,.15)} 50%{background:rgba(200,64,48,.3)} }
    @keyframes countUp  { from{opacity:0;transform:scale(.7)} to{opacity:1;transform:scale(1)} }

    .fu  { animation: fadeIn .4s ease both; }
    .su  { animation: slideUp .35s ease both; }
    .pls { animation: pulse 1.5s ease infinite; }

    /* ── 噪点纹理背景 ── */
    body::before {
      content: '';
      position: fixed; inset: 0; pointer-events: none; z-index: 0;
      background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.035'/%3E%3C/svg%3E");
      opacity: 0.6;
    }

    /* ── 扫描线效果 ── */
    body::after {
      content: '';
      position: fixed; inset: 0; pointer-events: none; z-index: 0;
      background: repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,.03) 2px, rgba(0,0,0,.03) 4px);
    }

    /* ── 布局 ── */
    .app { position: relative; z-index: 1; min-height: 100vh; display: flex; flex-direction: column; }

    /* ── Header ── */
    .header {
      border-bottom: 1px solid var(--line);
      padding: 0 32px;
      height: 62px;
      display: flex; align-items: center; justify-content: space-between;
      background: var(--bg);
      position: sticky; top: 0; z-index: 100;
    }
    .logo-text {
      font-family: 'Playfair Display', serif;
      font-weight: 900; font-size: 22px;
      background: linear-gradient(135deg, var(--gold), var(--gold2));
      -webkit-background-clip: text; -webkit-text-fill-color: transparent;
      letter-spacing: -0.5px;
    }
    .logo-dot { width: 6px; height: 6px; background: var(--gold); border-radius: 50%; margin-right: 8px; animation: glow 2s ease infinite; }
    .slogan { font-size: 11px; color: var(--text3); letter-spacing: .8px; font-weight: 300; }

    .header-badges { display: flex; gap: 6px; }
    .hbadge {
      padding: 4px 10px; border-radius: 4px; font-size: 10px; font-weight: 700;
      letter-spacing: .6px; border: 1px solid;
    }

    /* ── 主区域 ── */
    .main { flex: 1; padding: 24px 28px; display: flex; flex-direction: column; gap: 20px; }

    /* ── 控制栏 ── */
    .controls {
      display: flex; gap: 12px; align-items: center; flex-wrap: wrap;
    }
    .ctrl-label { font-size: 10px; font-weight: 700; color: var(--text3); letter-spacing: .8px; text-transform: uppercase; }

    /* ── 风格选择器 ── */
    .style-btn {
      padding: 7px 16px; border-radius: 6px; font-size: 12px; font-weight: 600;
      border: 1px solid var(--line2); background: transparent; cursor: pointer;
      color: var(--text2); transition: all .15s; font-family: 'Noto Sans SC', sans-serif;
      letter-spacing: .3px;
    }
    .style-btn.on {
      background: var(--gold); color: var(--bg); border-color: var(--gold); font-weight: 700;
    }
    .style-btn:hover:not(.on) { border-color: var(--gold); color: var(--gold); }

    /* ── 强度滑块 ── */
    .intensity-wrap { display: flex; align-items: center; gap: 8px; margin-left: auto; }
    input[type=range] {
      -webkit-appearance: none; width: 100px; height: 3px;
      background: linear-gradient(to right, var(--gold) 0%, var(--gold) var(--val, 60%), var(--line2) var(--val, 60%));
      border-radius: 2px; outline: none; cursor: pointer;
    }
    input[type=range]::-webkit-slider-thumb {
      -webkit-appearance: none; width: 14px; height: 14px;
      background: var(--gold); border-radius: 50%; cursor: pointer;
    }

    /* ── 双栏工作区 ── */
    .workspace {
      display: grid; grid-template-columns: 1fr 1fr; gap: 16px; flex: 1;
    }
    @media (max-width: 760px) { .workspace { grid-template-columns: 1fr; } }

    .pane {
      background: var(--bg2); border: 1px solid var(--line);
      border-radius: 14px; display: flex; flex-direction: column;
      overflow: hidden; position: relative;
    }
    .pane-header {
      padding: 12px 16px; border-bottom: 1px solid var(--line);
      display: flex; align-items: center; justify-content: space-between;
      background: var(--bg3);
    }
    .pane-title {
      font-size: 11px; font-weight: 700; letter-spacing: .8px;
      color: var(--text3); text-transform: uppercase; display: flex; align-items: center; gap: 6px;
    }
    .pane-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }

    textarea {
      flex: 1; background: transparent; border: none; outline: none; resize: none;
      color: var(--text1); font-family: 'Noto Sans SC', sans-serif;
      font-size: 14px; line-height: 1.85; padding: 18px 18px;
      min-height: 340px;
    }
    textarea::placeholder { color: var(--text3); font-weight: 300; }
    textarea:read-only { color: var(--text2); cursor: default; }

    /* ── AI 概率计量器 ── */
    .meter-bar {
      height: 3px; background: var(--line);
      border-radius: 0 0 14px 14px; overflow: hidden;
    }
    .meter-fill {
      height: 100%; border-radius: inherit;
      transition: width .8s ease, background .5s ease;
    }

    /* ── 标红 AI 痕迹 ── */
    .ai-highlight {
      background: rgba(200,64,48,.18); border-bottom: 1.5px solid var(--red);
      border-radius: 2px; padding: 0 1px;
    }

    /* ── 主按钮 ── */
    .btn-main {
      display: inline-flex; align-items: center; justify-content: center; gap: 8px;
      background: linear-gradient(135deg, var(--gold), var(--gold2));
      color: var(--bg); font-family: 'Noto Sans SC', sans-serif;
      font-weight: 700; font-size: 14px; letter-spacing: .3px;
      border: none; border-radius: 10px; padding: 13px 32px; cursor: pointer;
      transition: all .18s; box-shadow: 0 4px 20px rgba(200,168,80,.25);
    }
    .btn-main:not(:disabled):hover {
      transform: translateY(-1px); box-shadow: 0 8px 30px rgba(200,168,80,.4);
    }
    .btn-main:disabled { opacity: .4; cursor: not-allowed; }

    .btn-icon {
      display: inline-flex; align-items: center; gap: 5px;
      background: transparent; border: 1px solid var(--line2); border-radius: 7px;
      color: var(--text2); font-size: 11px; font-weight: 600; padding: 5px 12px;
      cursor: pointer; transition: all .14s; font-family: 'Noto Sans SC', sans-serif;
    }
    .btn-icon:hover { border-color: var(--gold); color: var(--gold); }

    /* ── 状态条 ── */
    .status-bar {
      border: 1px solid var(--line2); border-radius: 10px;
      background: var(--bg3); overflow: hidden;
    }
    .status-inner { padding: 14px 18px; }
    .status-phase {
      display: flex; align-items: center; gap: 10px; margin-bottom: 10px;
    }
    .status-icon { font-size: 16px; flex-shrink: 0; }
    .status-text { font-size: 13px; font-weight: 600; color: var(--text1); flex: 1; }
    .status-loop {
      font-size: 10px; font-weight: 700; color: var(--gold);
      border: 1px solid var(--gold); border-radius: 4px; padding: 2px 8px; letter-spacing: .5px;
    }

    .status-steps { display: flex; flex-direction: column; gap: 6px; }
    .step-item {
      display: flex; align-items: center; gap: 10px;
      font-size: 12px; color: var(--text3); transition: color .3s;
    }
    .step-item.done  { color: var(--green); }
    .step-item.active { color: var(--text1); }
    .step-item.active .step-text { animation: pulse 1.2s ease infinite; }
    .step-dot {
      width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0;
      background: var(--line2); transition: background .3s;
    }
    .step-item.done  .step-dot { background: var(--green); }
    .step-item.active .step-dot { background: var(--gold); animation: glow 1s ease infinite; }

    .progress-track { height: 3px; background: var(--line); border-radius: 2px; overflow: hidden; }
    .progress-fill {
      height: 100%; background: linear-gradient(90deg, var(--gold), var(--gold2));
      border-radius: 2px; transition: width .5s ease;
    }

    /* ── 结果区域 ── */
    .result-pane { background: var(--bg2); border: 1px solid var(--line); border-radius: 14px; }

    /* ── 评分展示 ── */
    .score-display {
      display: flex; align-items: center; gap: 8px; padding: 8px 12px;
      border-radius: 8px; font-size: 11px; font-weight: 700;
    }
    .score-ring {
      width: 36px; height: 36px; border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      font-family: 'DM Mono', monospace; font-weight: 500; font-size: 12px;
      border: 2px solid; flex-shrink: 0;
    }

    /* ── 结果统计 ── */
    .stats-row {
      display: flex; gap: 16px; flex-wrap: wrap;
      padding: 12px 18px; border-top: 1px solid var(--line);
      background: var(--bg3);
    }
    .stat-item { display: flex; flex-direction: column; gap: 2px; }
    .stat-val { font-family: 'DM Mono', monospace; font-size: 16px; font-weight: 500; }
    .stat-lbl { font-size: 9px; color: var(--text3); font-weight: 700; letter-spacing: .6px; text-transform: uppercase; }

    /* ── 历史记录 ── */
    .history-item {
      padding: 10px 14px; border-bottom: 1px solid var(--line);
      cursor: pointer; transition: background .14s; display: flex; gap: 10px; align-items: center;
    }
    .history-item:hover { background: var(--bg3); }
    .history-item:last-child { border-bottom: none; }

    /* ── Toast ── */
    .toast {
      position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%);
      background: var(--bg3); border: 1px solid var(--green);
      color: var(--green); padding: 10px 20px; border-radius: 8px;
      font-size: 13px; font-weight: 600; z-index: 999;
      animation: slideUp .3s ease;
    }

    /* ── 词汇对比 ── */
    .word-diff { display: flex; flex-wrap: wrap; gap: 4px; padding: 14px 18px; }
    .word-chip {
      padding: 3px 8px; border-radius: 5px; font-size: 12px;
      font-family: 'DM Mono', monospace;
    }
    .word-chip.removed { background: rgba(200,64,48,.2); color: var(--red); text-decoration: line-through; }
    .word-chip.added   { background: rgba(72,168,120,.2); color: var(--green); }

    /* ── 检测结果标签 ── */
    .detect-tags { display: flex; flex-wrap: wrap; gap: 5px; padding: 8px 12px; }
    .dtag {
      padding: 2px 9px; border-radius: 4px; font-size: 10px; font-weight: 700;
      letter-spacing: .4px;
    }
    .dtag.high   { background: rgba(200,64,48,.2);  color: var(--red);   border: 1px solid rgba(200,64,48,.3); }
    .dtag.medium { background: rgba(200,168,80,.2); color: var(--gold);  border: 1px solid rgba(200,168,80,.3); }
    .dtag.low    { background: rgba(72,168,120,.2); color: var(--green); border: 1px solid rgba(72,168,120,.3); }

    .divider { width: 1px; height: 24px; background: var(--line); }
    .spinning { animation: spin .7s linear infinite; display: inline-block; }
  `}</style>
);

/*  */
// callClaude now routes through server-side proxy (API key stays secure)
async function callClaude(messages, system = "") {
  const { callAI: _callAI } = await import("./aiClient");
  return _callAI(messages, system, 3000, "qwen");
}

/* 模拟 AI 检测（返回0-100的AI概率分数） */
async function detectAI(text, prevScore = null) {
  // 真实场景应调用 Sapling/ZeroGPT API
  // 这里用启发式规则模拟
  const aiPhrases = [
    "首先","其次","最后","总之","综上所述","不可否认","在当今社会",
    "众所周知","希望本文","总的来说","值得注意","毫无疑问","显而易见",
    "随着时代","与此同时","总而言之","不言而喻","由此可见","在某种程度上",
  ];
  const formalWords = ["务必","深入探讨","充分体现","有效提升","切实保障","积极推进"];

  let score = 40; // 基础分

  // AI 高频词
  aiPhrases.forEach(p => { if (text.includes(p)) score += 6; });
  formalWords.forEach(p => { if (text.includes(p)) score += 4; });

  // 段落结构分析：过于工整的分号/逗号
  const paraCount = text.split(/[\n\r]+/).filter(l => l.trim().length > 0).length;
  const avgLen = text.length / Math.max(paraCount, 1);
  if (avgLen > 80 && avgLen < 120) score += 10; // 过于统一的段落长度
  if (text.split("，").length > text.split("。").length * 3) score += 8;

  // 口语化特征（降分）
  const colloquial = ["哈哈","诶","嗯","吧？","啊","对了","说实话","说白了","真的","好吧","欸"];
  colloquial.forEach(p => { if (text.includes(p)) score -= 5; });
  if (text.match(/[😂🤣😭💀🔥✨🤩😍]/u)) score -= 8;
  if (text.includes("？") && text.split("？").length > 2) score -= 5; // 多个问句

  // 如果有前一轮分数，在其基础上下降（模拟重写效果）
  if (prevScore !== null) {
    const reduction = 15 + Math.random() * 20;
    score = Math.max(8, prevScore - reduction);
  }

  score = Math.round(Math.max(5, Math.min(95, score + (Math.random() - 0.5) * 10)));
  await new Promise(r => setTimeout(r, 600 + Math.random() * 400)); // 模拟延迟
  return score;
}

/* 核心重写 System Prompt */
/*  */
const TS_PRICE   = "9.9";
const TS_CONTACT = "X79Y99";         
const TS_SK      = "textsoul_v1";

/* ── 收款二维码（和简历工具共用同一张即可）── */
const TS_QR_SINGLE = "";   // single tool QR
const TS_QR_BUNDLE = "";   // bundle QR
// ═══════════════════════════════════════════════════════════════════
// 【密令管理区】只需修改这里，其他代码不用动
// expires: "2026-12-31" = 到期日  |  expires: null = 永不过期
// 新增密令：https://emn178.github.io/online-tools/sha256.html 生成哈希后加一行
// ═══════════════════════════════════════════════════════════════════
const TS_SLOTS = [
  // ── TextSoul 专属密令（50个）──
  { hash: "0ac5237c35a62fd585395aa1639b4fec000caa8e8fb4914ab93e981f31acdc33", expires: "2026-12-31" }, // soul_01: dfcxj3
  { hash: "d84e4c4264261a20dc7087057a5dbc32d63554c3a5ad2ac699041889dc30263f", expires: "2026-12-31" }, // soul_02: PpHB0zS6
  { hash: "6149955e52deb363c121c357a2e71ad35c00a6d0cea41fdea38fe9e2501e93ca", expires: "2026-12-31" }, // soul_03: rvdWT2j
  { hash: "b5f260dafc122f3a1429bfba02e36ca4e9ff7b0e2925e3fe5505b40ba747143c", expires: "2026-12-31" }, // soul_04: W0DP4TD
  { hash: "f37f3b41733a8be0762611238fd03d3c0d700f2c90e28ab3df848334d3840948", expires: "2026-12-31" }, // soul_05: ZHXgDMb
  { hash: "e712249403da3a5442c6ea9ca424fdd3a763ded38feca8b5f876fd4b58233fc2", expires: "2026-12-31" }, // soul_06: 0YHs1ZC
  { hash: "bd4e304690fe34e0187d6add729021252f03e76f4f92466ff2329caeeede9cd2", expires: "2026-12-31" }, // soul_07: 9wLMBO
  { hash: "a577ba49206fe5546d2658218875e2e29f052def3b72f8769ef8d61d3fdfa082", expires: "2026-12-31" }, // soul_08: 0ys7wST4
  { hash: "bf2d89ebb35ca2b8fb8c0fe32edada5f4dc269757b0be1d09122072d22c4a9ef", expires: "2026-12-31" }, // soul_09: Byabqz
  { hash: "0bc9c6012f6a1f14c791130975b099adf93dcf3fc3711449348591faf7d81808", expires: "2026-12-31" }, // soul_10: 8KIc9ghq
  { hash: "ff83b2009f92449664854b31dd37ff12d7a2b1a6252788929c8668464429e280", expires: "2026-12-31" }, // soul_11: dK0GCSP
  { hash: "70d94a0f8755617653b90ef80482a2842573c4200abfffd7e0724dc6f7aa8e35", expires: "2026-12-31" }, // soul_12: pm42i01N
  { hash: "272690c6a5685515433e51e919d7e04763a5c36fc64955350f7c02c5612dc0a9", expires: "2026-12-31" }, // soul_13: GOEeIDLU
  { hash: "b84e52a3e6317a87a27d30fe8ac60692f51496146476deee9c7a3e8f8d433cae", expires: "2026-12-31" }, // soul_14: tANeZpI
  { hash: "ef08485374d8250158d8f86e1989f58f815d304ff6524452e5556caccf177244", expires: "2026-12-31" }, // soul_15: HNrAAkVF
  { hash: "88ea60f0d2d73c294f4fda0198900ab63a9b0902fe42c17f01d77de68c983acf", expires: "2026-12-31" }, // soul_16: Z4FCKF2S
  { hash: "f96e1159187bdb7fedf251e4e1533799434ccde7893fb29bbf66da5817c6645d", expires: "2026-12-31" }, // soul_17: DdIyDxq
  { hash: "2a786faffb901de3dc7deb2cdab17e8ad79b9a066795694c2c24dd9a1c1fa129", expires: "2026-12-31" }, // soul_18: QPCi29
  { hash: "41c97543dcf4a8cc2b876106e09bebf4d3308de8824a22c66e55c8c58eb29da6", expires: "2026-12-31" }, // soul_19: 9BAzcv
  { hash: "07f79df325580a6d9e44233671db61b3d308754e654c6d9834a0c5f920441ff7", expires: "2026-12-31" }, // soul_20: hKDyCcO
  { hash: "e5e80503a4f2dbe25dad3f0efcac52e7223a2e232adc8fcea923a20ef41af352", expires: "2026-12-31" }, // soul_21: JzyJMQj
  { hash: "dc8bab0799dd627eedef17fba8ea0e584b351959a4d18a9c1480cdd8c3d92b79", expires: "2026-12-31" }, // soul_22: U3d4Rj7X
  { hash: "01f987af4221edf2b07e2899dc88f6645632c2eec6ddae0d28b9e3eff2718d92", expires: "2026-12-31" }, // soul_23: 5RbF3MnX
  { hash: "43497e38020d5992f45b440f1d51d4c5f982e60cf45671afe821feac8999dc17", expires: "2026-12-31" }, // soul_24: tvRkiX
  { hash: "d1f744c20a057446c272d52a235047007ce535d2242785bb7b3e9aef5ea2e746", expires: "2026-12-31" }, // soul_25: ZfkPNh
  { hash: "0bc7ebae8df51fde35aa6a60e9adc58caa64563baebacb7f5a63e02b07733c3d", expires: "2026-12-31" }, // soul_26: 2OGYIL
  { hash: "7c704ae559b31bc559e8264422cc3788e1778696a27297302a15c31dc5b604b7", expires: "2026-12-31" }, // soul_27: 5e9unmNq
  { hash: "93566b18f017c26a2b02f23308d93c144b31b0b666deba5903839be58a0e896e", expires: "2026-12-31" }, // soul_28: hw8Vv5j
  { hash: "a4328e22ffdee5d8cfadd93f78e7aa86aff63d4a635c4be7bd695f0d79cb96fa", expires: "2026-12-31" }, // soul_29: gsofd12f
  { hash: "d25c9df7eb5d4352cb28fb2072a74f93fd418cfc81e1191608dddd35f045f497", expires: "2026-12-31" }, // soul_30: DtrGRFOY
  { hash: "64391f76a989e7b0b86492ef8ee2e55ba04ad0d125590ee4d9adcd25f67de2bd", expires: "2026-12-31" }, // soul_31: qSTCnIgT
  { hash: "6180ac748ebd81bc9049580a4abd76ba1654bbd04bfcc94de298ea6fbd0a1cdf", expires: "2026-12-31" }, // soul_32: 9uAVi5
  { hash: "730b139df815a9711c8140ae7a7bb5515d859762e339a47424b756f3a753cfba", expires: "2026-12-31" }, // soul_33: oLpheR
  { hash: "17207e8de4d523d5c3c39d9a3ed7c8667c5b8e76231e472010e8a3b9a681a98f", expires: "2026-12-31" }, // soul_34: bM9Q0D5
  { hash: "02eea6e24571c1d69c8e4f7fc71c17b7c54b132202c2dbdc818c9dded3f32023", expires: "2026-12-31" }, // soul_35: iZEuUHo
  { hash: "c228d0ae32f18e15e685380f851e88c8d0d72ddc8211110892d3237342cb001e", expires: "2026-12-31" }, // soul_36: PYSRDS1
  { hash: "2d731228e455208bd1e15a0edb71e4d8ae241fcd12424a219d6c72d6079e6dce", expires: "2026-12-31" }, // soul_37: 6gOBYtg
  { hash: "63350052f53b3d77f68901eadc821af3eb49d9c401a4d504eadd5b0342f29d70", expires: "2026-12-31" }, // soul_38: ixvf2OAF
  { hash: "9e8ecfd2193343d6f4b6f0022f3d38bc71f3f03519050d81d60d0d910a52ded1", expires: "2026-12-31" }, // soul_39: nuLtPs
  { hash: "55b78599c1750e18f5d07117e37385ef64a974763ebb065f3c7b755772c50194", expires: "2026-12-31" }, // soul_40: vzT0yK
  { hash: "ce3656a4f8f1acafacc2a6033ba516e7e327fa604bbf1db3f2b76271894a402d", expires: "2026-12-31" }, // soul_41: ohOqjU9
  { hash: "49ce532015565e115e9a2c2492097b9a54aa9be4903e6ad0809c06fccda3f3dd", expires: "2026-12-31" }, // soul_42: fbQ1PAP
  { hash: "4932f036b86936e2757ed6a6c5bfbb8a31a3c263e23112d2ab5ce67171168eec", expires: "2026-12-31" }, // soul_43: mLPZLaX
  { hash: "15767da44479ff7d59e4c71006017fd4d51be9d11ef0046cfcd3a62fc73d3cf4", expires: "2026-12-31" }, // soul_44: pbSSGa
  { hash: "1aac19f0a6a557b50ce1809df1f8c8481bdeec673e92b03c4202b9ba85b6035d", expires: "2026-12-31" }, // soul_45: iR7kFK
  { hash: "6aa5537b48701b35aa335b55118100a6ac7661daf87ef8d13922a126a75564e5", expires: "2026-12-31" }, // soul_46: idqkJG9l
  { hash: "d7bdc50837052d54c213085bbf045d95973f350a81e4602947f2fee7665664ff", expires: "2026-12-31" }, // soul_47: CZw6Ni
  { hash: "c80bb2577f81cf4ca2600843e2b66701a672c73df8c37400aa53e52958e66478", expires: "2026-12-31" }, // soul_48: 4hpWyZbM
  { hash: "92c2db7a83aa815ec747e55f7866cd825c82ee6451721e8bb5d6c5b3fc233121", expires: "2026-12-31" }, // soul_49: 8gJTBX5
  { hash: "7c4d407829a9f78a9cbaf982aca5f1877f1c49f412d66121a35bbfc341580717", expires: "2026-12-31" }, // soul_50: XNw3ql
  // ── 三合一套餐（50个）── 三个工具都能用
  { hash: "58852f6a56b025b0cd9ac272a07d2b5d7c00d91f7d3468e5d01efd334ff152f2", expires: "2026-12-31" }, // bundle_01: 0cEDhk
  { hash: "a532d5100bd2221eb99b1d8798463eaf027f4480b2f52201468072374236ec4d", expires: "2026-12-31" }, // bundle_02: rHgEqLNs
  { hash: "2a3ee50e7856ca852a541591fc1fa765e71a277f41f80842ed24cc5ca7ea6d82", expires: "2026-12-31" }, // bundle_03: heIdo2c
  { hash: "7320595c93c7afb29ff3fa73da39ac0a410e425b8011b3c0e293923c235caea3", expires: "2026-12-31" }, // bundle_04: drHId8ia
  { hash: "4260d0510ce65a89f3e0007a06df3ef81fa1c55ebb51a465c1a708d06b584764", expires: "2026-12-31" }, // bundle_05: 0Xikx3o
  { hash: "52b7a3d82cd50dd846285e0a96c3d62361a0c8a76ce535924c5c1ce3af6ab133", expires: "2026-12-31" }, // bundle_06: S3vSbTl
  { hash: "d1ba3f041d5fe410f47a812aea85b5d1dbfab31ecfb5efc9ee541e4e4fba2c3f", expires: "2026-12-31" }, // bundle_07: WqvXp2pG
  { hash: "6cdd8745823b2497e9e8613f85e17518d989d2a2c4f789384c03c18111f8aa32", expires: "2026-12-31" }, // bundle_08: VFjxqn
  { hash: "01781a622e636a1da0d5d43fc7009afc822cf49e7efd30d1ebc00a5e129f4652", expires: "2026-12-31" }, // bundle_09: LkP1JD5F
  { hash: "264b5c83d72e1be9259f5c2ed6c678f4beb3aa66476a7eb816e4f5af1fcf71e1", expires: "2026-12-31" }, // bundle_10: xZYACdP
  { hash: "13a2f4212cf79eeb0da218cf8e7a051334e7151e421d92f6f92b1c1371f42063", expires: "2026-12-31" }, // bundle_11: b6milAy
  { hash: "5c1d3c357474db6ca1f6ab83057b029476e5319ee7c2f5dfa3f619c8fd9b97fa", expires: "2026-12-31" }, // bundle_12: IsScV7
  { hash: "1b2cfa5c1a319af4415b81a04056ba90c4c4097daeb91b51f7d26d10982a600e", expires: "2026-12-31" }, // bundle_13: RbWljos4
  { hash: "665bcc8cbf827c6cb8219c5290dfd6699c5b03074f86192096603339ea04d325", expires: "2026-12-31" }, // bundle_14: 94cQpJ
  { hash: "cdaf5b35eca921486558e522056a44e0f87b98d5b91eac25a272c76bd808a2d7", expires: "2026-12-31" }, // bundle_15: IMWNWi
  { hash: "848db25e07ac778bc9a6348a322a4278ef253472cb46724ddfc7e2c37aa415e8", expires: "2026-12-31" }, // bundle_16: l6ycMTR
  { hash: "315a9bfd7a49fc1b60887e84fff7a17224b75001ebb1937895b3b74aafd21d58", expires: "2026-12-31" }, // bundle_17: D0QR7NZ
  { hash: "826fcada952d869788335119b62c99fa5b78729af1fffd020e8f2073853dba01", expires: "2026-12-31" }, // bundle_18: ClhNAEE
  { hash: "67be03865906827bfbcfe289f71ba5c42baa5fc5fe9578ff202c066eb57e5631", expires: "2026-12-31" }, // bundle_19: UTWUUgx
  { hash: "9a86db34888fddd08a97a1d9afd2991aae7e0aabece009432543a479fcf5da1c", expires: "2026-12-31" }, // bundle_20: rZ92vfvg
  { hash: "6c9902fff46080cef3de43ef37a7711b0a0a70c8c724c807b7570787f685cc7d", expires: "2026-12-31" }, // bundle_21: RBfHwE
  { hash: "6657152d15b88a211587cc3615190d1f71853810b44c67697f70660550000e36", expires: "2026-12-31" }, // bundle_22: Z4DxlGX2
  { hash: "d106a985922fbcd3471f958cf1402575095ff0e95767b6c7028ff10fa573c38e", expires: "2026-12-31" }, // bundle_23: pYJ6lW
  { hash: "c49f4981e4a074eede4298d38be2f450ffebc13a1010cc46175b01a21189df63", expires: "2026-12-31" }, // bundle_24: 795v4Eu
  { hash: "006cec3f3db67ff0e3fe9461ca1ced69dea62fc8215bc7cba920c62a9d2b5a97", expires: "2026-12-31" }, // bundle_25: ImSleiC
  { hash: "792a29e23d04e7d712fa393697f68ed877c077084abbc63729a948946aae3293", expires: "2026-12-31" }, // bundle_26: 2frKYJFR
  { hash: "e2555dfe8f855bde18b76e542d1d63f64119a7f042547ef6278e4cc30489e9b6", expires: "2026-12-31" }, // bundle_27: 5CgEZxQ
  { hash: "019ff2d0e515afe70754618f3f4ad87f6050daba55e2bc7c26bc354f10dda6d1", expires: "2026-12-31" }, // bundle_28: 1udglz
  { hash: "ef58498ab5c679d71c94550456d5911a8984d84940a71b32c373e5f6754729d4", expires: "2026-12-31" }, // bundle_29: SgpZDG
  { hash: "049c56dc2c2abfb14d651fa14f96a02c77d1d5847caba5316eeb7c1077d26161", expires: "2026-12-31" }, // bundle_30: G8II2s
  { hash: "76d3be96592da36ad462a4c5cab6231351af66a68dce11edd248680a254bf604", expires: "2026-12-31" }, // bundle_31: 1luiY72
  { hash: "e8fde2b707cf66a31d886c34f9b324a5acc77d8af26f93e27be23279ae8c8004", expires: "2026-12-31" }, // bundle_32: tsq7gas
  { hash: "7dcd1d15897734e1777633a4b7180454f8d898ddf5eeeb5f1db5e5e4262e3cc3", expires: "2026-12-31" }, // bundle_33: oM980t3I
  { hash: "7c20a5951bfba07c8d1b28f06df624ef5a87354edfdd1c13140650b1fcbb41a0", expires: "2026-12-31" }, // bundle_34: dKN94k
  { hash: "1169547ff4ed0cd9115c782152f62752b4a20a8ad5a7ccee49161f155139be76", expires: "2026-12-31" }, // bundle_35: mo5IJyFu
  { hash: "14d143b5802041d0ffd7b116d349a4b4f3047c92dd89b1e57cbbbf0a1bd0e75d", expires: "2026-12-31" }, // bundle_36: o8xPBX
  { hash: "b6c9aed6243a31d2aa044a89b8a77c4ad8b00b5d56136b31d569a49e86bcac43", expires: "2026-12-31" }, // bundle_37: Fp925V
  { hash: "fb526263de18fd09ac557f1e05b618476c5d6525af1a1ec60c69e6629c951d90", expires: "2026-12-31" }, // bundle_38: c2RSAd
  { hash: "5ca66339f6bab8377cd71aa930552765df3484076dee77b7ca96dc4ecd5268f6", expires: "2026-12-31" }, // bundle_39: PUudzd
  { hash: "d68ab55d88e56e8f08ac0de3d42c29783fc1cbd764e8e0a60777dfae874c9f15", expires: "2026-12-31" }, // bundle_40: ayqx9mJ
  { hash: "43ee433eb2258fdc141d9eaf22b86c5deaa3ca6560aa8fb09429fb846c4aa0ee", expires: "2026-12-31" }, // bundle_41: ClNG4oVe
  { hash: "1163b6f653c2d98e48346197c0e3bb0a76e2dd15a1c1480618a1cc93feb5d7e9", expires: "2026-12-31" }, // bundle_42: j5Wqd4
  { hash: "8ab8a9556b62a8d8904ab20d181d4aee03a670ec2cc4041f527c85260ad452f6", expires: "2026-12-31" }, // bundle_43: PiAIKP
  { hash: "d7bdad3f7ce8705de9e6ddd5f213099f2695be92ea60b67dba589150b5fa37be", expires: "2026-12-31" }, // bundle_44: ica2rOpx
  { hash: "632a3da7c4d537b87240354c9a7941731a7a1dc1e6136493519ed3722bbaf173", expires: "2026-12-31" }, // bundle_45: QyaDAVZs
  { hash: "5907ed8aa403cd105d7093506cfe88dbc4bea92b8ce82070f1b6714899d1cb52", expires: "2026-12-31" }, // bundle_46: PfzVnz
  { hash: "62f4b0596d1183f04e2ce29e6005da8f22bbfdf87ec165bed2c3cb6ab7b8e727", expires: "2026-12-31" }, // bundle_47: xtcvz7VW
  { hash: "8a1544a64535241d60de9edd415286c9c335498d58a55699e82450b64258aaf3", expires: "2026-12-31" }, // bundle_48: nCSDku
  { hash: "ec3116cfb0847a6e146b1d2317640d4011647de3ef12be924b7a40796a15e2b4", expires: "2026-12-31" }, // bundle_49: uL2FtEv
  { hash: "22dc92a9ec1ae254d929178cc20d570c93a061afe94bf528cab6a46a1fac181b", expires: "2026-12-31" }, // bundle_50: 32POVnq
];
function checkTsSlot(hash) {
  const slot = TS_SLOTS.find(s => s.hash === hash);
  if (!slot) return false;
  if (!slot.expires) return true;
  const expiry = new Date(slot.expires);
  expiry.setHours(23, 59, 59, 999);
  return new Date() <= expiry;
}
async function tsSha(s){const b=await crypto.subtle.digest("SHA-256",new TextEncoder().encode(s));return Array.from(new Uint8Array(b)).map(x=>x.toString(16).padStart(2,"0")).join("");}
const tsUnlocked=()=>{try{return localStorage.getItem(TS_SK)==="true";}catch{return false;}};
const tsSetUnlock=()=>{try{localStorage.setItem(TS_SK,"true");}catch{}};

function TSGate({onUnlock,onClose}){
  const[pw,setPw]=useState("");
  const[err,setErr]=useState(false);
  const[ok,setOk]=useState(false);
  const[qr,setQr]=useState(null);
  const sub=async()=>{
    if(!pw.trim())return;
    const h=await tsSha(pw.trim());
    if(checkTsSlot(h)){setOk(true);tsSetUnlock();setTimeout(onUnlock,1200);}
    else{setErr(true);setTimeout(()=>setErr(false),1500);}
  };
  return(
    <div onClick={e=>{if(e.target===e.currentTarget)onClose();}} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.9)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",backdropFilter:"blur(10px)"}}>
      <div style={{background:"#111",border:"1px solid #2a2a2a",borderRadius:20,padding:"36px 32px",maxWidth:420,width:"90%",boxShadow:"0 24px 80px #000"}}>
        {ok?(<div style={{textAlign:"center",padding:"20px 0"}}><div style={{fontSize:52}}>🎉</div><div style={{fontSize:20,fontWeight:800,color:"#22c55e",margin:"12px 0 6px"}}>解锁成功！</div><div style={{fontSize:13,color:"#555"}}>TextSoul 全部功能已开放</div></div>):(
        <div>
          <div style={{textAlign:"center",marginBottom:20}}>
            <div style={{fontSize:32,marginBottom:8}}>✍️</div>
            <div style={{fontSize:18,fontWeight:800,color:"#e8e8e8",marginBottom:8}}>TextSoul 去AI化 · 付费功能</div>
          {/* QR 弹层 */}
          {qr && (
            <div onClick={()=>setQr(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.85)",zIndex:10001,display:"flex",alignItems:"center",justifyContent:"center",backdropFilter:"blur(6px)"}}>
              <div onClick={e=>e.stopPropagation()} style={{background:"#111",border:"1px solid #2a2a2a",borderRadius:20,padding:"28px 24px",textAlign:"center",maxWidth:280,width:"90%"}}>
                <div style={{fontSize:13,fontWeight:800,color:"#e8e8e8",marginBottom:4}}>
                  {qr==="single"?"单工具 ¥"+TS_PRICE:"三工具套餐 ¥19.9"}
                </div>
                <div style={{fontSize:11,color:"#555",marginBottom:16}}>扫码付款后截图联系获取密令</div>
                <img src={qr==="single"?TS_QR_SINGLE:TS_QR_BUNDLE} alt="收款码"
                  style={{width:180,height:180,borderRadius:12,border:"2px solid #333",background:"#fff",display:"block",margin:"0 auto 16px",objectFit:"contain"}}
                  onError={e=>{e.target.style.display="none";}}
                />
                <div style={{fontSize:12,color:"#f59e0b",fontWeight:700}}>{TS_CONTACT}</div>
                <button onClick={()=>setQr(null)} style={{marginTop:14,width:"100%",background:"none",border:"1px solid #333",color:"#555",borderRadius:8,padding:"8px",fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>关闭</button>
              </div>
            </div>
          )}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
            <div onClick={()=>setQr("single")} style={{background:"rgba(251,146,60,.08)",border:"1.5px solid rgba(251,146,60,.25)",borderRadius:12,padding:"12px 10px",textAlign:"center",cursor:"pointer"}}
              onMouseEnter={e=>e.currentTarget.style.borderColor="rgba(251,146,60,.7)"}
              onMouseLeave={e=>e.currentTarget.style.borderColor="rgba(251,146,60,.25)"}>
              <div style={{fontSize:11,color:"#fb923c",fontWeight:700,marginBottom:4}}>单工具</div>
              <div style={{display:"flex",alignItems:"baseline",gap:2,justifyContent:"center"}}>
                <span style={{fontSize:13,color:"#fb923c",fontWeight:600}}>¥</span>
                <span style={{fontSize:24,fontWeight:900,color:"#fb923c"}}>{TS_PRICE}</span>
              </div>
              <div style={{fontSize:10,color:"#7a5030",marginTop:2}}>仅去AI化工具</div>
              <div style={{fontSize:10,color:"#fb923c",marginTop:4,opacity:.7}}>点击扫码付款 →</div>
            </div>
            <div onClick={()=>setQr("bundle")} style={{background:"rgba(16,185,129,.08)",border:"1.5px solid rgba(16,185,129,.3)",borderRadius:12,padding:"12px 10px",textAlign:"center",cursor:"pointer",position:"relative"}}
              onMouseEnter={e=>e.currentTarget.style.borderColor="rgba(16,185,129,.7)"}
              onMouseLeave={e=>e.currentTarget.style.borderColor="rgba(16,185,129,.3)"}>
              <div style={{position:"absolute",top:-8,left:"50%",transform:"translateX(-50%)",background:"#10b981",color:"#fff",fontSize:9,fontWeight:800,padding:"2px 8px",borderRadius:20}}>推荐</div>
              <div style={{fontSize:11,color:"#10b981",fontWeight:700,marginBottom:4}}>三工具套餐</div>
              <div style={{display:"flex",alignItems:"baseline",gap:2,justifyContent:"center"}}>
                <span style={{fontSize:13,color:"#10b981",fontWeight:600}}>¥</span>
                <span style={{fontSize:24,fontWeight:900,color:"#10b981"}}>19.9</span>
              </div>
              <div style={{fontSize:10,color:"#3a7a5a",marginTop:2}}>学习+简历+去AI化</div>
              <div style={{fontSize:10,color:"#10b981",marginTop:4,opacity:.7}}>点击扫码付款 →</div>
            </div>
          </div>
            <div style={{fontSize:13,color:"#555",lineHeight:1.7}}>对抗循环去AI化 · 4种平台风格<br/>付款后联系获取解锁密令 🔑</div>
          </div>
          <div style={{background:"#0a0a0a",border:"1px solid #222",borderRadius:12,padding:"14px 18px",marginBottom:16}}>
            <div style={{fontSize:11,color:"#444",fontWeight:700,marginBottom:6}}>📨 联系方式</div>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <div style={{background:"#111",border:"1px solid #333",borderRadius:10,padding:"10px 16px",flex:1}}>
                <div style={{fontSize:11,color:"#555",marginBottom:2}}>账号</div>
                <div style={{fontSize:16,fontWeight:800,color:"#f59e0b",fontFamily:"monospace",letterSpacing:1}}>{TS_CONTACT}</div>
              </div>
              <button onClick={()=>navigator.clipboard?.writeText(TS_CONTACT)} style={{background:"none",border:"1px solid #333",color:"#555",borderRadius:8,padding:"8px 14px",fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>复制</button>
            </div>
          </div>
          <div style={{marginBottom:14}}>
            <div style={{fontSize:11,color:"#444",fontWeight:700,marginBottom:6}}>🔑 已有密令？输入解锁</div>
            <input type="password" placeholder="输入密令..." value={pw} onChange={e=>{setPw(e.target.value);setErr(false);}} onKeyDown={e=>e.key==="Enter"&&sub()} style={{width:"100%",background:"#0a0a0a",border:`1.5px solid ${err?"#ef4444":"#333"}`,borderRadius:10,color:"#e8e8e8",padding:"11px 16px",fontSize:14,fontFamily:"inherit",outline:"none",letterSpacing:2,boxSizing:"border-box"}}/>
            {err&&<div style={{fontSize:12,color:"#ef4444",marginTop:5}}>密令不正确，请重试</div>}
          </div>
          <div style={{display:"flex",gap:10}}>
            <button onClick={sub} style={{flex:1,background:pw?"linear-gradient(135deg,#92400e,#f59e0b)":"#1a1a0a",color:pw?"#fff":"#444",border:"none",borderRadius:10,padding:"12px",fontSize:14,fontWeight:700,cursor:pw?"pointer":"default",fontFamily:"inherit"}}>解锁</button>
            <button onClick={onClose} style={{background:"none",border:"1px solid #333",color:"#555",borderRadius:10,padding:"12px 16px",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>稍后</button>
          </div>
        </div>)}
      </div>
    </div>
  );
}

const SYSTEM_PROMPTS = {
  "通用": `你是资深专栏编辑和反AI检测专家。将用户文本重写，彻底消除AI生成痕迹，使其完美通过检测并符合人类真实表达。

【绝对禁止】：首先、其次、总之、综上所述、不可否认、在当今社会、众所周知、希望本文、值得注意、毫无疑问、总的来说、随着时代、与此同时、总而言之

【提升突发性(Burstiness)】：打破对称段落结构，混合3-5字极短句和长复合句，允许思路突然跳跃

【提升困惑度(Perplexity)】：放弃"最正确"词汇，偶尔用冷门同义词或口语化比喻

【保留不完美】：适度加入"说实话""对了""哎"等语气词，允许使用网络梗

【情绪注入】：赋予主观情绪（吐槽/惊喜/无奈），避免绝对客观说教语调

除重写正文外，不要输出任何解释。`,

  "小红书": `你是小红书资深创作者。将文本重写成真实用户风格，绝对禁止AI高频词。

【小红书特有风格】：
- 自然插入emoji（句首或句尾，不要整齐排列）
- 用"姐妹/宝子"等称呼，偶尔用"我的天/绝了/救命"
- 多用感叹句和问句，制造互动感
- 段落短，最多3行换一次
- 可以用"⚠️注意""💡小tip""✨真心推荐"等格式

【绝对禁止AI词汇】：首先、其次、综上、总之、不可否认

除重写正文外，不要输出任何解释。`,

  "知乎": `你是知乎资深答主，擅长把复杂知识讲得有趣不装逼。

【知乎风格】：
- 用"说个结论先"或直接切入核心
- 夹带个人经历或具体细节（即使是虚构的合理细节）
- 偶尔说"这里有个坑"或"划重点"
- 允许有轻微吐槽，比较接地气
- 数据和案例优先，拒绝空话
- 可以加"——"破折号做补充说明

【绝对禁止】：综上所述、不可否认、众所周知、毫无疑问

除重写正文外，不要输出任何解释。`,

  "微信公众号": `你是头部公众号编辑，文章有10W+经验。

【公众号风格】：
- 开头要有钩子，前两句话就要抓住读者
- 段落不超过4行，适当加粗关键词（用**词**标注）
- 用"你知道吗"或"换你，你怎么选？"增加代入感
- 结尾有行动号召或留悬念
- 多用对比和转折："不是A，而是B"
- 可以加小标题分隔（用【】括起来）

【绝对禁止】：随着时代、在当今社会、毫无疑问、众所周知

除重写正文外，不要输出任何解释。`,
};

/* AI 词组提取（用于标红） */
const AI_MARKERS = [
  "首先","其次","最后","总之","综上所述","不可否认","在当今社会","众所周知",
  "希望本文","值得注意","毫无疑问","总的来说","随着时代","与此同时","总而言之",
  "不言而喻","由此可见","有效提升","切实保障","积极推进","深入探讨","充分体现",
  "务必","着重指出","总体而言","至关重要","不可忽视",
];

/*  */
function ScoreRing({ score, size = 36 }) {
  const color = score >= 60 ? "var(--red)" : score >= 30 ? "var(--gold)" : "var(--green)";
  const label = score >= 60 ? "高危" : score >= 30 ? "可疑" : "安全";
  return (
    <div className="score-ring" style={{ width: size, height: size, borderColor: color, color, fontSize: size === 36 ? 12 : 14 }}>
      {score}
    </div>
  );
}

function DetectTags({ text }) {
  const found = AI_MARKERS.filter(m => text.includes(m));
  if (found.length === 0) return <span style={{ fontSize: 11, color: "var(--green)", padding: "8px 12px", display: "block" }}>✓ 未检测到 AI 高频词</span>;
  return (
    <div className="detect-tags">
      {found.slice(0, 12).map((t, i) => (
        <span key={i} className="dtag high">{t}</span>
      ))}
      {found.length > 12 && <span className="dtag medium">+{found.length - 12}</span>}
    </div>
  );
}

const STEPS_META = [
  { id: "detect0",  icon: "🔍", text: "首次 AI 痕迹检测" },
  { id: "rewrite1", icon: "✍️", text: "第 1 轮人工化重写" },
  { id: "detect1",  icon: "🔍", text: "第 1 轮检测验证" },
  { id: "rewrite2", icon: "✍️", text: "第 2 轮深度重写" },
  { id: "detect2",  icon: "🔍", text: "第 2 轮检测验证" },
  { id: "rewrite3", icon: "✍️", text: "第 3 轮极限优化" },
  { id: "detect3",  icon: "🔍", text: "最终质量验证" },
];

/*  */
export default function TextSoul() {
  const [unlocked, setUnlocked] = useState(tsUnlocked);
  const [showGate, setShowGate] = useState(false);
  const [input,      setInput]      = useState("");
  const [output,     setOutput]     = useState("");
  const [style,      setStyle]      = useState("通用");
  const [intensity,  setIntensity]  = useState(70);
  const [running,    setRunning]    = useState(false);
  const [phase,      setPhase]      = useState(null);   // null | 'detecting' | 'rewriting' | 'done' | 'error'
  const [stepsDone,  setStepsDone]  = useState([]);
  const [activeStep, setActiveStep] = useState(null);
  const [loopCount,  setLoopCount]  = useState(0);
  const [scores,     setScores]     = useState([]);     // [{round, before, after}]
  const [progress,   setProgress]   = useState(0);
  const [statusMsg,  setStatusMsg]  = useState("");
  const [toast,      setToast]      = useState("");
  const [history,    setHistory]    = useState([]);
  const [showHistory,setShowHistory]= useState(false);
  const [activeTab,  setActiveTab]  = useState("result"); // result | diff | history

  const inputRef = useRef(null);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2200);
  };

  const copyOutput = () => {
    if (!output) return;
    navigator.clipboard?.writeText(output);
    showToast("✓ 已复制到剪贴板");
  };

  const copyInput = () => {
    if (!input) return;
    navigator.clipboard?.writeText(input);
    showToast("✓ 已复制原文");
  };

  const finalScore = scores.length > 0 ? scores[scores.length - 1].score : null;
  const initScore  = scores.length > 0 ? scores[0].score : null;
  const scoreColor = (s) => s >= 60 ? "var(--red)" : s >= 30 ? "var(--gold)" : "var(--green)";

  /* ── 核心：对抗循环 ── */
  const run = async () => {
    if (!input.trim()) return;
    setRunning(true);
    setPhase("running");
    setOutput("");
    setScores([]);
    setStepsDone([]);
    setActiveStep(null);
    setLoopCount(0);
    setProgress(0);

    const MAX = 3;
    const THRESHOLD = 30;
    let currentText = input;
    let roundScores = [];

    try {
      // ── Round 0: 初次检测 ──
      setActiveStep("detect0");
      setStatusMsg("🔍 正在破解文本 DNA，扫描 AI 特征指纹...");
      setProgress(5);

      let score = await detectAI(currentText);
      roundScores = [{ round: 0, score }];
      setScores([...roundScores]);
      setStepsDone(["detect0"]);
      setProgress(12);

      if (score < THRESHOLD) {
        setActiveStep(null);
        setOutput(currentText);
        setPhase("done");
        setStatusMsg(`✅ 原文 AI 概率仅 ${score}%，已接近人类写作，无需处理`);
        setProgress(100);
        setRunning(false);
        return;
      }

      // ── 对抗循环 ──
      for (let round = 1; round <= MAX; round++) {
        if (score < THRESHOLD) break;
        setLoopCount(round);

        // 重写
        const rwKey = `rewrite${round}`;
        setActiveStep(rwKey);
        setProgress(12 + (round - 1) * 28);

        const phaseLabels = [
          "正在注入口语化特征，破坏 AI 句式对称...",
          "深度重构段落节奏，植入情绪波动...",
          "极限优化词汇多样性，消除最后的 AI 痕迹...",
        ];
        setStatusMsg(`✍️ 第 ${round} 轮拟人化重写 — ${phaseLabels[round - 1]}`);

        const intensityNote = intensity > 75
          ? "（高强度模式：大幅改写，允许扩写细节）"
          : intensity > 45
          ? "（标准模式：保留核心意思，改写表达方式）"
          : "（保守模式：微调措辞，尽量保留原文结构）";

        const prompt = `当前AI概率分数：${score}%（目标降至30%以下）${intensityNote}
目标平台风格：${style}

需要重写的文本：
${currentText}

请按照系统指令完成重写，直接输出重写后的正文，不要加任何说明。`;

        currentText = await callClaude(
          [{ role: "user", content: prompt }],
          SYSTEM_PROMPTS[style] || SYSTEM_PROMPTS["通用"]
        );

        setStepsDone(prev => [...prev, rwKey]);
        setProgress(12 + (round - 1) * 28 + 14);
        setOutput(currentText);

        // 检测
        const dtKey = `detect${round}`;
        setActiveStep(dtKey);
        setStatusMsg(`🔍 第 ${round} 轮验证 — 再次扫描 AI 痕迹...`);

        const newScore = await detectAI(currentText, score);
        score = newScore;
        roundScores = [...roundScores, { round, score }];
        setScores([...roundScores]);
        setStepsDone(prev => [...prev, dtKey]);
        setProgress(12 + round * 28);

        if (score < THRESHOLD) break;
      }

      // 完成
      setActiveStep(null);
      setPhase("done");
      setOutput(currentText);
      setProgress(100);

      const finalS = roundScores[roundScores.length - 1]?.score || score;
      const initS  = roundScores[0]?.score || 0;
      const reduced = initS - finalS;

      if (finalS < THRESHOLD) {
        setStatusMsg(`🎉 成功！AI 概率从 ${initS}% → ${finalS}%，降低了 ${reduced} 个百分点`);
      } else {
        setStatusMsg(`⚠️ 已完成 ${MAX} 轮优化，AI 概率降至 ${finalS}%（建议手动微调）`);
      }

      // 保存到历史
      setHistory(prev => [{
        id: Date.now(), input: input.slice(0, 60) + "...",
        output: currentText, style,
        initScore: initS, finalScore: finalS,
        rounds: roundScores.length - 1, time: new Date().toLocaleTimeString(),
      }, ...prev].slice(0, 10));

    } catch (e) {
      setPhase("error");
      setStatusMsg(`❌ 出错了：${e.message}`);
    } finally {
      setRunning(false);
    }
  };

  /* 词汇差异分析 */
  const getDiff = () => {
    if (!input || !output) return [];
    const inputWords  = input.replace(/[，。！？、""''（）【】\n]/g, " ").split(/\s+/).filter(Boolean);
    const outputWords = output.replace(/[，。！？、""''（）【】\n]/g, " ").split(/\s+/).filter(Boolean);
    const inputSet  = new Set(inputWords);
    const outputSet = new Set(outputWords);
    const removed = [...inputSet].filter(w => !outputSet.has(w) && w.length > 1).slice(0, 20);
    const added   = [...outputSet].filter(w => !inputSet.has(w) && w.length > 1).slice(0, 20);
    return { removed, added };
  };

  const diff = getDiff();
  const wordReductionPct = output ? Math.round((1 - output.length / Math.max(input.length, 1)) * 100) : 0;

  return (
    <>
      <S />
      {showGate && <TSGate onUnlock={()=>{setUnlocked(true);setShowGate(false);}} onClose={()=>setShowGate(false)} />}
      {!unlocked && (
        <div onClick={()=>setShowGate(true)} style={{position:"fixed",bottom:80,right:24,zIndex:100,background:"rgba(251,146,60,.12)",border:"1px solid rgba(251,146,60,.3)",borderRadius:12,padding:"10px 16px",cursor:"pointer",backdropFilter:"blur(8px)"}}>
          <div style={{fontSize:12,fontWeight:700,color:"#fb923c"}}>{"\uD83D\uDD12 \u4ED8\u8D39\u89E3\u9501 \xA5" + TS_PRICE}</div>
          <div style={{fontSize:10,color:"#7a5030",marginTop:2}}>\u70B9\u51FB\u67E5\u770B\u8BE6\u60C5</div>
        </div>
      )}
      <div className="app">

        {/* ── HEADER ── */}
        <header className="header">
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div className="logo-dot" />
              <span className="logo-text">TextSoul</span>
            </div>
            <div className="divider" />
            <span className="slogan">剔除机器味，注入灵魂的文案重塑引擎</span>
          </div>
          <div className="header-badges">
            <span className="hbadge" style={{ borderColor: "var(--gold)", color: "var(--gold)" }}>
              对抗循环 v2
            </span>
            <span className="hbadge" style={{ borderColor: "var(--line2)", color: "var(--text3)" }}>
              MAX×3
            </span>
          </div>
        </header>

        <main className="main fu">

          {/* ── 控制栏 ── */}
          <div className="controls">
            <span className="ctrl-label">目标风格</span>
            {["通用", "小红书", "知乎", "微信公众号"].map(s => (
              <button key={s} className={`style-btn ${style === s ? "on" : ""}`} onClick={() => setStyle(s)}>
                {s === "小红书" ? "✨ 小红书" : s === "知乎" ? "💡 知乎" : s === "微信公众号" ? "📱 公众号" : "⚡ 通用"}
              </button>
            ))}
            <div className="divider" />
            <div className="intensity-wrap">
              <span className="ctrl-label">改写强度</span>
              <input type="range" min={20} max={100} value={intensity}
                style={{ "--val": `${intensity}%` }}
                onChange={e => setIntensity(+e.target.value)} />
              <span style={{ fontSize: 12, fontFamily: "'DM Mono',monospace", color: "var(--gold)", minWidth: 28 }}>
                {intensity}
              </span>
            </div>
            <div style={{ marginLeft: "auto" }}>
              <button className="btn-icon" onClick={() => setShowHistory(!showHistory)}>
                📜 历史 ({history.length})
              </button>
            </div>
          </div>

          {/* ── 双栏工作区 ── */}
          <div className="workspace">

            {/* ── 左栏：输入 + 检测 ── */}
            <div className="pane">
              <div className="pane-header">
                <div className="pane-title">
                  <div className="pane-dot" style={{ background: "var(--red)" }} />
                  输入区 · AI 痕迹检测
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  {initScore !== null && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <ScoreRing score={initScore} />
                      <div>
                        <div style={{ fontSize: 10, color: "var(--text3)", lineHeight: 1 }}>初始AI概率</div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: scoreColor(initScore) }}>
                          {initScore}%
                        </div>
                      </div>
                    </div>
                  )}
                  <button className="btn-icon" onClick={copyInput}>复制</button>
                </div>
              </div>

              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                placeholder="将带有 AI 痕迹的文案粘贴到这里...&#10;&#10;支持任意格式，系统会自动识别 AI 写作模式并进行针对性处理。"
                disabled={running}
              />

              {/* AI 痕迹标签 */}
              {input && !running && (
                <div style={{ borderTop: "1px solid var(--line)" }}>
                  <div style={{ padding: "8px 12px 4px", fontSize: 10, color: "var(--text3)", fontWeight: 700, letterSpacing: .6 }}>
                    AI 高频词检测
                  </div>
                  <DetectTags text={input} />
                </div>
              )}

              {/* 字符统计 */}
              <div style={{
                padding: "8px 16px", borderTop: "1px solid var(--line)",
                display: "flex", justifyContent: "space-between", alignItems: "center",
              }}>
                <span style={{ fontSize: 11, color: "var(--text3)", fontFamily: "'DM Mono',monospace" }}>
                  {input.length} 字
                </span>
                <button
                  className="btn-main"
                  onClick={()=>{ if(!unlocked){setShowGate(true);return;} run(); }}
                  disabled={running || !input.trim()}
                  style={{ padding: "9px 24px", fontSize: 13 }}
                >
                  {running
                    ? <><span className="spinning">⟳</span> 处理中</>
                    : "⚡ 检测并拟人化"}
                </button>
              </div>

              {/* 进度条 */}
              <div className="meter-bar">
                <div className="meter-fill" style={{
                  width: `${progress}%`,
                  background: progress === 100
                    ? (finalScore < 30 ? "var(--green)" : "var(--gold)")
                    : "linear-gradient(90deg,var(--gold),var(--gold2))",
                }} />
              </div>
            </div>

            {/* ── 右栏：输出 ── */}
            <div className="pane">
              <div className="pane-header">
                <div className="pane-title">
                  <div className="pane-dot" style={{
                    background: phase === "done" && finalScore < 30 ? "var(--green)" : "var(--gold)",
                  }} />
                  输出区 · 拟人化结果
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  {finalScore !== null && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <ScoreRing score={finalScore} />
                      <div>
                        <div style={{ fontSize: 10, color: "var(--text3)", lineHeight: 1 }}>最终AI概率</div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: scoreColor(finalScore) }}>
                          {finalScore}%
                        </div>
                      </div>
                    </div>
                  )}
                  <button className="btn-icon" onClick={copyOutput} disabled={!output}>一键复制</button>
                </div>
              </div>

              <textarea
                value={output}
                readOnly
                placeholder="拟人化后的文本将显示在这里...&#10;&#10;系统会通过最多 3 轮对抗循环，将文本的 AI 概率降到安全阈值以下。"
              />

              {/* 底部统计 */}
              {output && phase === "done" && (
                <div className="stats-row">
                  <div className="stat-item">
                    <div className="stat-val" style={{ color: "var(--green)" }}>
                      {initScore}% → {finalScore}%
                    </div>
                    <div className="stat-lbl">AI 概率变化</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-val" style={{ color: "var(--gold)" }}>
                      {scores.length - 1}轮
                    </div>
                    <div className="stat-lbl">对抗循环</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-val" style={{ color: "var(--blue)" }}>
                      {output.length}字
                    </div>
                    <div className="stat-lbl">输出字数</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-val" style={{
                      color: finalScore < 30 ? "var(--green)" : finalScore < 60 ? "var(--gold)" : "var(--red)",
                    }}>
                      {finalScore < 30 ? "✓ 安全" : finalScore < 60 ? "⚠ 可疑" : "✗ 高危"}
                    </div>
                    <div className="stat-lbl">检测状态</div>
                  </div>
                </div>
              )}

              <div className="meter-bar">
                <div className="meter-fill" style={{
                  width: phase === "done" ? "100%" : "0%",
                  background: finalScore < 30 ? "var(--green)" : "var(--gold)",
                }} />
              </div>
            </div>
          </div>

          {/* ── 状态条 ── */}
          {(running || phase === "done" || phase === "error") && (
            <div className="status-bar su">
              <div className="status-inner">
                <div className="status-phase">
                  <span className="status-icon">
                    {phase === "done" ? (finalScore < 30 ? "✅" : "⚠️") : phase === "error" ? "❌" : <span className="spinning">⟳</span>}
                  </span>
                  <span className="status-text">{statusMsg}</span>
                  {running && loopCount > 0 && (
                    <span className="status-loop">第 {loopCount} 轮</span>
                  )}
                  <div className="progress-track" style={{ width: 120 }}>
                    <div className="progress-fill" style={{ width: `${progress}%` }} />
                  </div>
                </div>

                {/* 步骤列表 */}
                <div className="status-steps">
                  {STEPS_META.slice(0, Math.max(
                    stepsDone.length + 2,
                    phase === "done" ? STEPS_META.length : 0
                  )).map(step => {
                    const isDone   = stepsDone.includes(step.id);
                    const isActive = activeStep === step.id;
                    // 只展示有关的步骤
                    const stepRound = parseInt(step.id.replace(/[a-z]/g, ""));
                    const maxVisible = (scores.length) * 2 + 1;
                    if (!isDone && !isActive && stepRound > scores.length) return null;
                    return (
                      <div key={step.id} className={`step-item ${isDone ? "done" : isActive ? "active" : ""}`}>
                        <div className="step-dot" />
                        <span className="step-text">{step.icon} {step.text}</span>
                        {isDone && scores.find((_, i) => {
                          const idx = step.id.startsWith("detect") ? parseInt(step.id.slice(6)) : -1;
                          return idx >= 0 && scores[idx];
                        }) && (() => {
                          const idx = step.id.startsWith("detect") ? parseInt(step.id.slice(6)) : -1;
                          const s = idx >= 0 ? scores[idx] : null;
                          return s ? (
                            <span style={{
                              marginLeft: "auto", fontSize: 11,
                              fontFamily: "'DM Mono',monospace", fontWeight: 600,
                              color: scoreColor(s.score),
                            }}>
                              {s.score}%
                            </span>
                          ) : null;
                        })()}
                      </div>
                    );
                  })}
                </div>

                {/* 分数折线 */}
                {scores.length > 1 && (
                  <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 10, color: "var(--text3)", fontWeight: 700, letterSpacing: .5 }}>AI概率变化</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      {scores.map((s, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          <div style={{
                            width: 32, height: 32, borderRadius: "50%",
                            background: `${scoreColor(s.score)}22`,
                            border: `1.5px solid ${scoreColor(s.score)}`,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 11, fontFamily: "'DM Mono',monospace",
                            fontWeight: 600, color: scoreColor(s.score),
                            animation: "countUp .4s ease both",
                            animationDelay: `${i * .1}s`,
                          }}>
                            {s.score}
                          </div>
                          {i < scores.length - 1 && (
                            <span style={{ fontSize: 12, color: "var(--text3)" }}>→</span>
                          )}
                        </div>
                      ))}
                    </div>
                    {scores.length > 1 && (
                      <span style={{
                        fontSize: 11, fontWeight: 700,
                        color: scores[0].score - scores[scores.length-1].score > 0 ? "var(--green)" : "var(--red)",
                      }}>
                        {scores[0].score - scores[scores.length-1].score > 0 ? "↓" : "↑"}
                        {Math.abs(scores[0].score - scores[scores.length-1].score)}%
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── 结果分析面板 ── */}
          {phase === "done" && output && (
            <div className="result-pane su">
              {/* Tab 栏 */}
              <div style={{
                borderBottom: "1px solid var(--line)", display: "flex",
                padding: "0 16px",
              }}>
                {[
                  { id: "result", label: "📊 评分详情" },
                  { id: "diff",   label: "🔄 词汇对比" },
                  { id: "guide",  label: "💡 优化建议" },
                ].map(t => (
                  <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
                    padding: "10px 16px", background: "transparent", border: "none",
                    borderBottom: `2px solid ${activeTab === t.id ? "var(--gold)" : "transparent"}`,
                    color: activeTab === t.id ? "var(--gold)" : "var(--text3)",
                    fontSize: 12, fontWeight: 700, cursor: "pointer", letterSpacing: .3,
                    fontFamily: "'Noto Sans SC',sans-serif", transition: "all .15s",
                  }}>{t.label}</button>
                ))}
              </div>

              {/* 评分详情 */}
              {activeTab === "result" && (
                <div style={{ padding: "16px 20px", display: "flex", gap: 16, flexWrap: "wrap" }}>
                  {scores.map((s, i) => (
                    <div key={i} style={{
                      background: "var(--bg3)", border: "1px solid var(--line2)",
                      borderRadius: 10, padding: "12px 16px", minWidth: 120, textAlign: "center",
                    }}>
                      <div style={{ fontSize: 10, color: "var(--text3)", marginBottom: 6, letterSpacing: .5 }}>
                        {i === 0 ? "初始检测" : `第 ${i} 轮检测`}
                      </div>
                      <div style={{
                        fontSize: 28, fontFamily: "'DM Mono',monospace",
                        fontWeight: 500, color: scoreColor(s.score),
                        lineHeight: 1, marginBottom: 4,
                      }}>{s.score}%</div>
                      <div style={{
                        height: 4, background: "var(--line)", borderRadius: 2, overflow: "hidden", margin: "6px 0",
                      }}>
                        <div style={{
                          height: "100%", background: scoreColor(s.score),
                          width: `${s.score}%`, borderRadius: 2, transition: "width 1s ease",
                        }} />
                      </div>
                      <div style={{ fontSize: 11, color: scoreColor(s.score), fontWeight: 700 }}>
                        {s.score >= 60 ? "高危" : s.score >= 30 ? "可疑" : "安全 ✓"}
                      </div>
                    </div>
                  ))}
                  <div style={{
                    background: `${scoreColor(finalScore)}12`,
                    border: `1px solid ${scoreColor(finalScore)}30`,
                    borderRadius: 10, padding: "12px 16px", display: "flex",
                    flexDirection: "column", justifyContent: "center", flex: 1, minWidth: 180,
                  }}>
                    <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 6 }}>综合结论</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: scoreColor(finalScore), marginBottom: 4 }}>
                      {finalScore < 30 ? "🎉 拟人化成功！" : finalScore < 60 ? "⚠️ 建议再次优化" : "❌ 需要手动调整"}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--text2)", lineHeight: 1.7 }}>
                      {finalScore < 30
                        ? `经过 ${scores.length - 1} 轮对抗优化，AI 概率降至 ${finalScore}%，低于安全阈值 30%。可以安全使用。`
                        : finalScore < 60
                        ? `AI 概率降至 ${finalScore}%，接近安全线。建议再次运行或手动调整关键句式。`
                        : `当前 AI 概率 ${finalScore}%，仍偏高。可以尝试：① 增大改写强度 ② 切换目标风格 ③ 手动添加口语化表达`}
                    </div>
                  </div>
                </div>
              )}

              {/* 词汇对比 */}
              {activeTab === "diff" && (
                <div style={{ padding: "16px 20px" }}>
                  <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "var(--red)", letterSpacing: .5, marginBottom: 8 }}>
                        🗑️ 已替换/删除的 AI 词汇
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                        {diff.removed?.length > 0
                          ? diff.removed.map((w, i) => <span key={i} className="word-chip removed">{w}</span>)
                          : <span style={{ fontSize: 12, color: "var(--text3)" }}>（无明显删除）</span>}
                      </div>
                    </div>
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "var(--green)", letterSpacing: .5, marginBottom: 8 }}>
                        ✨ 新增的人性化词汇
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                        {diff.added?.length > 0
                          ? diff.added.map((w, i) => <span key={i} className="word-chip added">{w}</span>)
                          : <span style={{ fontSize: 12, color: "var(--text3)" }}>（无明显新增）</span>}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 优化建议 */}
              {activeTab === "guide" && (
                <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 8 }}>
                  {finalScore >= 30 && (
                    <div style={{
                      background: "rgba(200,168,80,.08)", border: "1px solid rgba(200,168,80,.2)",
                      borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "var(--text2)", lineHeight: 1.8,
                    }}>
                      <span style={{ color: "var(--gold)", fontWeight: 700 }}>💡 仍有优化空间</span>
                      <br />试着在输出文本中手动添加：① 具体的数字或日期 ② 个人经历片段 ③ 情绪化语气词（"哎""真的""服了"）
                    </div>
                  )}
                  <div style={{
                    background: "var(--bg3)", border: "1px solid var(--line2)",
                    borderRadius: 8, padding: "10px 14px",
                  }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", marginBottom: 8, letterSpacing: .6 }}>
                      避免使用的词汇（AI 检测器重点扫描）
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                      {AI_MARKERS.map((w, i) => (
                        <span key={i} className="dtag high">{w}</span>
                      ))}
                    </div>
                  </div>
                  <div style={{
                    background: "rgba(72,168,120,.06)", border: "1px solid rgba(72,168,120,.2)",
                    borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "var(--text2)", lineHeight: 1.8,
                  }}>
                    <span style={{ color: "var(--green)", fontWeight: 700 }}>✓ 人类写作特征清单</span>
                    <br />
                    <span>句子长短不一 · 有口语语气词 · 出现情绪波动 · 段落节奏不规律 · 偶有语法灵活处理 · 包含具体细节而非泛泛而谈</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── 历史记录 ── */}
          {showHistory && (
            <div className="result-pane su">
              <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--line)", display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text2)", letterSpacing: .5 }}>
                  📜 历史记录（{history.length} 条）
                </span>
                <button className="btn-icon" onClick={() => setHistory([])}>清空</button>
              </div>
              {history.length === 0 ? (
                <div style={{ padding: "24px", textAlign: "center", color: "var(--text3)", fontSize: 13 }}>暂无历史记录</div>
              ) : (
                history.map((h) => (
                  <div key={h.id} className="history-item" onClick={() => {
                    setInput(h.input.replace("...", ""));
                    setOutput(h.output);
                    setStyle(h.style);
                    setShowHistory(false);
                    setPhase("done");
                  }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
                      background: `${scoreColor(h.finalScore)}18`,
                      border: `1.5px solid ${scoreColor(h.finalScore)}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 11, fontWeight: 700, color: scoreColor(h.finalScore),
                      fontFamily: "'DM Mono',monospace",
                    }}>{h.finalScore}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, color: "var(--text1)", marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {h.input}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--text3)", display: "flex", gap: 10 }}>
                        <span>{h.time}</span>
                        <span>{h.style}</span>
                        <span>{h.initScore}% → {h.finalScore}%</span>
                        <span>{h.rounds} 轮</span>
                      </div>
                    </div>
                    <button className="btn-icon" onClick={e => {
                      e.stopPropagation();
                      navigator.clipboard?.writeText(h.output);
                      showToast("✓ 已复制历史结果");
                    }}>复制</button>
                  </div>
                ))
              )}
            </div>
          )}

        </main>

        {/* Toast */}
        {toast && <div className="toast">{toast}</div>}
      </div>
    </>
  );
}
